(function () {
  var SECTION_SELECTOR = '.oddit-search-drawer';
  var LIVE_HEADER_SELECTOR = 'x-header.header';
  var LIVE_SEARCH_TRIGGER = '.header__search-link a[aria-controls]';
  var DEBOUNCE_MS = 300;
  var debounceTimer = null;
  var currentController = null;
  var triggerElement = null;
  var syncProgress = null;
  var offsetRaf = null;
  var resetSearch = null;

  function getDrawer() {
    return document.querySelector(SECTION_SELECTOR);
  }

  // Desktop reuses the header's own search bar and drops the panel below the
  // header, so the panel has to start where the header ends.
  function syncHeaderOffset(drawer) {
    var header = document.querySelector('.oddit-header') || document.querySelector(LIVE_HEADER_SELECTOR);
    var offset = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
    drawer.style.setProperty('--oddit-search-drawer-top', offset + 'px');

    var panel = drawer.querySelector('.oddit-search-drawer__panel');
    if (!panel) return;
    // Animate max-height to the content's real height. Transitioning from a
    // viewport-sized value spends the first half of the tween clipping nothing.
    var content = panel.scrollHeight;
    drawer.style.setProperty('--oddit-search-drawer-height', content + 'px');

    // Scroll only when the content genuinely cannot fit below the header. Keying
    // this off the panel's current height instead makes a scrollbar flash while
    // max-height is still catching up to the content (during the open tween, and
    // again whenever images settle just after).
    var available = window.innerHeight - offset;
    panel.classList.toggle('is-scrollable', content > available + 1);
  }

  // The header animates in both directions (collapsing to the wide search field
  // on open, expanding back on close) and the page can scroll under it, so track
  // it every frame the drawer is on screen rather than on discrete events.
  function trackHeader(drawer) {
    if (offsetRaf) return;
    offsetRaf = requestAnimationFrame(function step() {
      if (drawer.style.display === 'none') {
        offsetRaf = null;
        return;
      }
      syncHeaderOffset(drawer);
      offsetRaf = requestAnimationFrame(step);
    });
  }

  function open() {
    var drawer = getDrawer();
    if (!drawer) return;
    triggerElement = document.activeElement;
    drawer.style.display = '';
    syncHeaderOffset(drawer);
    trackHeader(drawer);
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        drawer.classList.add('is-open');
        // Lock the page before focusing: focusing inside the scrollable panel
        // scrolls it into view, which moves the sticky header out from under
        // the offset we just measured and strands the panel below it.
        document.body.style.overflow = 'hidden';
        var input = drawer.querySelector('[data-search-input]');
        // Hidden on desktop — the header's field takes over there.
        if (input && input.offsetParent !== null) input.focus({ preventScroll: true });
        if (syncProgress) syncProgress();
        document.dispatchEvent(new CustomEvent('oddit:search-open'));
      });
    });
  }

  function close() {
    var drawer = getDrawer();
    if (!drawer) return;
    drawer.classList.remove('is-open');
    document.body.style.overflow = '';
    document.dispatchEvent(new CustomEvent('oddit:search-close'));
    var panel = drawer.querySelector('.oddit-search-drawer__panel');
    if (panel) {
      panel.addEventListener('transitionend', function handler() {
        panel.removeEventListener('transitionend', handler);
        if (!drawer.classList.contains('is-open')) {
          drawer.style.display = 'none';
        }
      }, { once: true });
    }
    if (triggerElement && triggerElement.focus && !triggerElement.hasAttribute('data-header-search-field')) {
      triggerElement.focus();
    }
    triggerElement = null;
    clearTimeout(debounceTimer);
    debounceTimer = null;
    if (currentController) {
      currentController.abort();
      currentController = null;
    }
    // Otherwise the next open still shows the previous query's results.
    if (resetSearch) resetSearch();
  }

  function toggle() {
    var drawer = getDrawer();
    if (!drawer) return;
    if (drawer.classList.contains('is-open')) {
      close();
    } else {
      open();
    }
  }

  function initPredictiveSearch(section) {
    // The desktop field lives in the header, outside this section.
    var inputs = document.querySelectorAll('[data-search-input]');
    var live = section.querySelector('[data-search-live]');
    var defaults = section.querySelector('[data-search-results]:not([data-search-live])');
    if (!inputs.length || !live) return;

    var types = section.getAttribute('data-search-resource-types') || 'product';
    var limit = section.getAttribute('data-search-results-limit') || '4';

    function showDefaults() {
      live.hidden = true;
      if (defaults) defaults.hidden = false;
      live.innerHTML = '';
    }

    resetSearch = showDefaults;

    function run(query, sourceInput) {
      if (currentController) currentController.abort();
      currentController = new AbortController();
      // Rendered by sections/oddit-search-results.liquid so the cards keep their
      // metafields and review ratings, which the JSON API cannot provide.
      var url =
        '/search/suggest?q=' +
        encodeURIComponent(query) +
        '&resources[type]=' + encodeURIComponent(types) +
        '&resources[limit]=' + encodeURIComponent(limit) +
        // Without limit_scope the limit is shared across all types, so products
        // get squeezed out by collection and article matches.
        '&resources[limit_scope]=each' +
        '&section_id=oddit-search-results';

      fetch(url, { signal: currentController.signal })
        .then(function (r) {
          if (!r.ok) throw new Error('Search request failed: ' + r.status);
          return r.text();
        })
        .then(function (html) {
          // A slower earlier request must not overwrite a newer query's results.
          if (sourceInput.value.trim() !== query) return;
          var fetched = new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector('[data-search-results]');
          if (!fetched) throw new Error('Search results markup missing');
          live.innerHTML = fetched.innerHTML;
          if (defaults) defaults.hidden = true;
          live.hidden = false;
        })
        .catch(function (err) {
          if (err.name === 'AbortError') return;
          // Leave the default panel up rather than showing a broken results list.
          showDefaults();
        });
    }

    for (var i = 0; i < inputs.length; i++) {
      inputs[i].addEventListener('input', function () {
        var sourceInput = this;
        var query = sourceInput.value.trim();
        clearTimeout(debounceTimer);
        if (currentController) {
          currentController.abort();
          currentController = null;
        }
        if (!query) {
          showDefaults();
          return;
        }
        debounceTimer = setTimeout(function () {
          run(query, sourceInput);
        }, DEBOUNCE_MS);
      });
    }
  }

  function initSection(section) {
    section.classList.toggle('oddit-search-drawer--standalone', !document.querySelector('.oddit-header'));

    var overlay = section.querySelector('[data-search-overlay]');
    if (overlay) {
      overlay.addEventListener('click', close);
    }

    // Desktop: the panel fills the viewport and carries the scrim, so clicks
    // on its empty area have to close the drawer too.
    var panel = section.querySelector('.oddit-search-drawer__panel');
    if (panel) {
      panel.addEventListener('click', function (e) {
        if (e.target.closest('[data-add-to-cart]')) {
          close();
          return;
        }
        if (e.target.closest('.oddit-search-drawer__results')) return;
        if (e.target.closest('.oddit-search-drawer__search-row')) return;
        close();
      });
    }

    var track = section.querySelector('.oddit-search-drawer__product-grid');
    var thumb = section.querySelector('.oddit-search-drawer__progress-bar > span');
    if (track && thumb) {
      syncProgress = function () {
        if (!track.scrollWidth) return; // drawer still hidden — nothing to measure
        var ratio = track.clientWidth / track.scrollWidth;
        thumb.style.width = (ratio * 100) + '%';
        thumb.style.marginLeft = (track.scrollLeft / track.scrollWidth * 100) + '%';
      };
      track.addEventListener('scroll', syncProgress, { passive: true });
      window.addEventListener('resize', syncProgress);
      syncProgress();
    }

    initPredictiveSearch(section);

    section.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
    });

  }

  var drawer = getDrawer();
  if (drawer) initSection(drawer);

  document.addEventListener(
    'click',
    function (e) {
      var trigger = e.target.closest(LIVE_SEARCH_TRIGGER);
      if (!trigger || !getDrawer()) return;
      e.preventDefault();
      e.stopPropagation();
      toggle();
    },
    true
  );

  window.OdditSearchDrawer = {
    open: open,
    close: close,
    toggle: toggle
  };
})();